import type { Project } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	ProjectEqualsFilterModifier,
	ProjectExcludesFilterModifier,
	ProjectIncludesFilterModifier,
	ProjectNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { ProjectFilterCell } from "./ProjectFilterCell"

type ProjectFieldValue = Project["uuid"]
type ProjectFilterValue = Project["uuid"][] | Project["uuid"]

export class ProjectFilterDef extends BaseFilterDef<ProjectFieldValue, ProjectFilterValue> {
	readonly id = "project"
	readonly defaultFilterValue: ProjectFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"
	readonly projects: Project[]

	constructor(projects: Project[]) {
		super()
		this.projects = projects
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: ProjectEqualsFilterModifier,
			isValueValid: (_value: ProjectFieldValue): _value is Project["uuid"] => true,
			isFilterValueValid: (filterValue: ProjectFilterValue): filterValue is Project["uuid"] =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: ProjectNotEqualsFilterModifier,
			isValueValid: (_value: ProjectFieldValue): _value is Project["uuid"] => true,
			isFilterValueValid: (filterValue: ProjectFilterValue): filterValue is Project["uuid"] =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: ProjectIncludesFilterModifier,
			isValueValid: (_value: ProjectFieldValue): _value is Project["uuid"] => true,
			isFilterValueValid: (filterValue: ProjectFilterValue): filterValue is Project["uuid"][] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: ProjectExcludesFilterModifier,
			isValueValid: (_value: ProjectFieldValue): _value is Project["uuid"] => true,
			isFilterValueValid: (filterValue: ProjectFilterValue): filterValue is Project["uuid"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<ProjectFilterDef>): ReactNode {
		return <ProjectFilterCell key={props.filter.id} {...props} />
	}
}
